import React, { useEffect, useMemo, useState } from "react";
import {
  FaBirthdayCake,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaEye,
  FaMagic,
  FaSave,
  FaSearch,
  FaUserSlash,
  FaUsers,
} from "react-icons/fa";
import api from "../api";
import { sanitizeDocumentTemplateHtml } from "../security/documentTemplateHtml";
import MarketingEmailEditor from "./MarketingEmailEditor";

const STYLE_PRESETS = [
  ["Afetuoso", "Tom acolhedor, humano e positivo, sem exageros."],
  ["Profissional", "Tom profissional, elegante e cordial."],
  ["Curto", "Mensagem curta, leve e objetiva."],
  ["Inspirador", "Tom inspirador, celebrando o novo ciclo com naturalidade."],
];

const formatBirthday = (value) => {
  const match = String(value || "").match(/^\d{4}-(\d{2})-(\d{2})$/);
  return match ? `${match[2]}/${match[1]}` : "—";
};

const formatNext = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
};

const draftFromSource = (source) => ({
  enabled: Boolean(source?.template?.enabled),
  sendHour: Number(source?.template?.sendHour ?? 8),
  audienceStatus: source?.template?.audienceStatus || "ACTIVE",
  theme: source?.template?.theme || "Feliz aniversário!",
  subject: source?.template?.subject || "Feliz aniversário, {{primeiro_nome}}! 🎉",
  previewText: source?.template?.previewText || "Uma mensagem especial para celebrar o seu dia.",
  html: source?.template?.html || "<p>Olá, <strong>{{primeiro_nome}}</strong>!</p><p>Desejamos um feliz aniversário e um novo ciclo cheio de realizações.</p>",
});

const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "AN";

const personMatchesAudience = (sourceKey, status, audienceStatus) => {
  if (audienceStatus === "ALL") return true;
  const normalized = String(status || "").toUpperCase();
  if (sourceKey === "HR") {
    const inactive = normalized === "INACTIVE" || normalized === "TERMINATED";
    return audienceStatus === "INACTIVE" ? inactive : !inactive;
  }
  return audienceStatus === "INACTIVE" ? normalized === "INACTIVE" : normalized === "ACTIVE";
};

function StepTitle({ number, title, description }) {
  return (
    <div className="flex items-start gap-3">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-[#071f54] text-xs font-black text-white">{number}</span>
      <div>
        <h4 className="text-sm font-black text-slate-900">{title}</h4>
        <p className="mt-0.5 text-xs leading-5 text-slate-500">{description}</p>
      </div>
    </div>
  );
}

export default function CRMEmailMarketingBirthdays({ onReload }) {
  const [sources, setSources] = useState([]);
  const [activeKey, setActiveKey] = useState("");
  const [drafts, setDrafts] = useState({});
  const [aiPrompt, setAiPrompt] = useState("");
  const [stylePreset, setStylePreset] = useState("Afetuoso");
  const [busy, setBusy] = useState("");
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setBusy("load");
    setError("");
    try {
      const response = await api.get("/mail/marketing/birthdays");
      const nextSources = Array.isArray(response.data?.sources) ? response.data.sources : [];
      setSources(nextSources);
      setActiveKey((current) => nextSources.some((source) => source.key === current) ? current : (nextSources[0]?.key || ""));
      setDrafts((current) => {
        const next = { ...current };
        nextSources.forEach((source) => { next[source.key] = draftFromSource(source); });
        return next;
      });
    } catch (err) {
      setError(err.response?.data?.error || "Não foi possível carregar os aniversariantes.");
    } finally {
      setBusy("");
    }
  };

  useEffect(() => { load(); }, []);

  const activeSource = sources.find((source) => source.key === activeKey) || null;
  const draft = drafts[activeKey] || draftFromSource(activeSource);
  const updateDraft = (field, value) => setDrafts((current) => ({
    ...current,
    [activeKey]: { ...(current[activeKey] || draftFromSource(activeSource)), [field]: value },
  }));

  const audiencePeople = useMemo(() => {
    const people = Array.isArray(activeSource?.people) ? [...activeSource.people] : [];
    return people.filter((person) =>
      personMatchesAudience(activeSource?.key, person.status, draft.audienceStatus || "ACTIVE")
    );
  }, [activeSource, draft.audienceStatus]);

  const sortedPeople = useMemo(() => {
    const term = search.trim().toLowerCase();
    return audiencePeople
      .filter((person) => !term || String(person.name || "").toLowerCase().includes(term) || String(person.email || "").toLowerCase().includes(term))
      .sort((a, b) => new Date(a.nextBirthday || 0) - new Date(b.nextBirthday || 0));
  }, [audiencePeople, search]);

  const eligibleCount = audiencePeople.length;
  const suppressedCount = audiencePeople.filter((person) => person.suppressed).length;

  const audienceLabel = draft.audienceStatus === "ALL"
    ? "Todos os cadastros"
    : draft.audienceStatus === "INACTIVE"
      ? "Somente inativos"
      : "Somente ativos";

  const generate = async () => {
    if (!activeSource) return;
    setBusy("ai");
    setError("");
    setFeedback("");
    try {
      const styleInstruction = STYLE_PRESETS.find(([name]) => name === stylePreset)?.[1] || "";
      const prompt = [
        `Crie um e-mail de aniversário para ${activeSource.label}.`,
        "Use a variável {{primeiro_nome}} ao se dirigir à pessoa para manter a personalização automática.",
        "Não coloque uma data fixa no conteúdo.",
        aiPrompt.trim() || "Mensagem elegante, calorosa e curta para felicitar pelo aniversário.",
        `Estilo: ${styleInstruction}`,
      ].join("\n");
      const response = await api.post("/mail/marketing/generate", { prompt, audience: [] });
      const generatedSubject = response.data?.subject || draft.subject;
      const generatedHtml = sanitizeDocumentTemplateHtml(response.data?.html || draft.html);
      updateDraft("theme", response.data?.theme || "Feliz aniversário!");
      updateDraft("subject", generatedSubject.includes("{{") ? generatedSubject : "Feliz aniversário, {{primeiro_nome}}! 🎉");
      updateDraft("previewText", response.data?.previewText || draft.previewText);
      updateDraft("html", generatedHtml.includes("{{primeiro_nome}}")
        ? generatedHtml
        : `<p>Olá, <strong>{{primeiro_nome}}</strong>!</p>${generatedHtml}`);
      setFeedback("Mensagem criada com IA. Revise e salve para aplicar.");
      setShowAi(false);
    } catch (err) {
      setError(err.response?.data?.error || "Não foi possível gerar a mensagem de aniversário.");
    } finally {
      setBusy("");
    }
  };

  const save = async () => {
    if (!activeSource) return;
    if (!draft.subject.trim() || !sanitizeDocumentTemplateHtml(draft.html)) {
      setError("Informe assunto e conteúdo.");
      return;
    }
    setBusy("save");
    setError("");
    setFeedback("");
    try {
      const text = String(draft.html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      const response = await api.put("/mail/marketing/birthdays/" + activeSource.key, {
        ...draft,
        sendHour: Number(draft.sendHour),
        html: sanitizeDocumentTemplateHtml(draft.html),
        text,
      });
      const sync = response.data?.sync || {};
      setFeedback(
        draft.enabled
          ? `Automação do módulo salva · ${Number(sync.scheduled || 0)} novo(s), ${Number(sync.updated || 0)} atualizado(s) e ${Number(sync.canceled || 0)} retirado(s) do agendamento.`
          : "Automação desativada. Os agendamentos futuros foram cancelados.",
      );
      await load();
      await onReload?.();
    } catch (err) {
      setError(err.response?.data?.error || "Não foi possível salvar a automação.");
    } finally {
      setBusy("");
    }
  };

  if (busy === "load" && !sources.length) {
    return <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center text-sm font-bold text-slate-400">Carregando aniversariantes...</div>;
  }

  if (!sources.length) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <FaBirthdayCake className="mx-auto text-3xl text-slate-300" />
        <h3 className="mt-3 font-black text-slate-800">Nenhum módulo disponível</h3>
        <p className="mt-1 text-sm text-slate-500">Os aniversariantes aparecem conforme os módulos habilitados e suas permissões.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">{error}</div>}
      {feedback && <div className="flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700"><FaCheckCircle /> {feedback}</div>}

      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-lg text-blue-600"><FaBirthdayCake /></span>
            <div>
              <h3 className="font-black text-slate-900">Aniversariantes</h3>
              <p className="mt-0.5 text-xs text-slate-500">Configure uma vez por módulo. O sistema inclui automaticamente todos os cadastros que atendam ao filtro escolhido.</p>
            </div>
          </div>
          <div className="flex flex-wrap rounded-2xl bg-slate-100 p-1">
            {sources.map((source) => (
              <button
                key={source.key}
                type="button"
                onClick={() => { setActiveKey(source.key); setFeedback(""); setError(""); setSearch(""); }}
                className={`rounded-xl px-4 py-2 text-xs font-extrabold transition ${activeKey === source.key ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              >
                {source.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 divide-x divide-slate-100 sm:grid-cols-4">
          <div className="px-5 py-3"><span className="block text-[10px] font-extrabold uppercase tracking-wide text-slate-400">No público</span><strong className="mt-1 block text-lg text-slate-900">{eligibleCount}</strong></div>
          <div className="px-5 py-3"><span className="block text-[10px] font-extrabold uppercase tracking-wide text-slate-400">Agendados</span><strong className="mt-1 block text-lg text-blue-700">{activeSource?.scheduledCount || 0}</strong></div>
          <div className="px-5 py-3"><span className="block text-[10px] font-extrabold uppercase tracking-wide text-slate-400">Cancelaram</span><strong className="mt-1 block text-lg text-rose-600">{suppressedCount}</strong></div>
          <div className="px-5 py-3"><span className="block text-[10px] font-extrabold uppercase tracking-wide text-slate-400">Status</span><strong className={`mt-1 block text-sm ${draft.enabled ? "text-emerald-700" : "text-slate-500"}`}>{draft.enabled ? "Automático" : "Desativado"}</strong></div>
        </div>
      </section>

      <div className="grid gap-4 xl:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="self-start rounded-3xl border border-slate-200 bg-white p-4 shadow-sm xl:sticky xl:top-3">
          <div className="flex items-center justify-between">
            <div>
              <strong className="text-sm text-slate-900">{activeSource?.label}</strong>
              <p className="text-xs text-slate-400">Próximos aniversários</p>
            </div>
            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-black text-blue-700"><FaUsers className="mr-1 inline" /> {eligibleCount}</span>
          </div>

          <label className="mt-3 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3">
            <FaSearch className="text-xs text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar nome ou e-mail"
              className="h-10 min-w-0 flex-1 bg-transparent text-xs text-slate-700 outline-none"
            />
          </label>

          <div className="mt-3 max-h-[680px] space-y-2 overflow-y-auto pr-1">
            {sortedPeople.map((person) => (
              <div key={activeSource.key + "-" + person.id} className={`rounded-2xl border p-3 ${person.suppressed ? "border-rose-100 bg-rose-50/60" : "border-slate-200 bg-white"}`}>
                <div className="flex gap-2.5">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-100 text-[10px] font-black text-slate-600">{initials(person.name)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <strong className="block truncate text-xs text-slate-800">{person.name}</strong>
                      {person.suppressed && <FaUserSlash className="shrink-0 text-rose-500" title="Cancelou o recebimento" />}
                    </div>
                    <span className="mt-0.5 block truncate text-[10px] text-slate-500">{person.email}</span>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2 text-[10px] font-bold">
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-slate-600"><FaCalendarAlt /> {formatBirthday(person.birthDate)}</span>
                  <span className={`truncate rounded-full px-2 py-1 ${person.suppressed ? "bg-rose-100 text-rose-700" : "bg-blue-50 text-blue-700"}`}>
                    {person.suppressed ? "Não recebe" : formatNext(person.nextBirthday)}
                  </span>
                </div>
              </div>
            ))}
            {!sortedPeople.length && <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-xs leading-5 text-slate-400">Nenhum aniversariante encontrado com data de nascimento e e-mail válido.</div>}
          </div>
        </aside>

        <main className="space-y-4">
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <StepTitle number="1" title="Público e automação" description="Escolha quem deste módulo participa da automação. Você não precisa selecionar pessoas individualmente." />
            <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_220px_170px]">
              <label className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-4 transition ${draft.enabled ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-slate-50"}`}>
                <input type="checkbox" checked={Boolean(draft.enabled)} onChange={(e) => updateDraft("enabled", e.target.checked)} className="h-5 w-5 rounded border-slate-300 text-blue-600" />
                <span className="min-w-0">
                  <strong className="block text-sm text-slate-800">{draft.enabled ? "Automação ativada" : "Automação desativada"}</strong>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    {draft.enabled
                      ? "Novos cadastros e alterações futuras entram automaticamente, sem precisar sincronizar manualmente."
                      : "Nenhum novo aniversário será enviado enquanto estiver desativada."}
                  </span>
                </span>
              </label>

              <label className="grid gap-1.5 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                <span className="text-[10px] font-extrabold uppercase tracking-wide text-slate-400">Quem deve receber</span>
                <select
                  value={draft.audienceStatus || "ACTIVE"}
                  onChange={(e) => updateDraft("audienceStatus", e.target.value)}
                  className="min-h-[38px] rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-extrabold text-slate-700 outline-none focus:border-blue-400"
                >
                  <option value="ACTIVE">Cadastros ativos</option>
                  <option value="INACTIVE">Cadastros inativos</option>
                  <option value="ALL">Todos os cadastros</option>
                </select>
                <span className="text-[10px] leading-4 text-slate-400">Somente com aniversário e e-mail válido preenchidos.</span>
              </label>

              <label className="grid gap-1.5 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                <span className="flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-wide text-slate-400"><FaClock /> Horário</span>
                <select value={draft.sendHour} onChange={(e) => updateDraft("sendHour", Number(e.target.value))} className="min-h-[38px] rounded-xl border border-slate-200 bg-blue-50 px-3 text-xs font-black text-blue-700 outline-none">
                  {Array.from({ length: 24 }, (_, hour) => <option key={hour} value={hour}>{String(hour).padStart(2, "0")}:00</option>)}
                </select>
                <span className="text-[10px] leading-4 text-slate-400">Horário usado no dia do aniversário.</span>
              </label>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl border border-blue-100 bg-blue-50/60 px-4 py-3 text-xs text-blue-800">
              <FaUsers className="shrink-0" />
              <strong>{audienceLabel}</strong>
              <span className="text-blue-600">·</span>
              <span>{eligibleCount} cadastro(s) com data de nascimento e e-mail válido entram nesta automação agora.</span>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <StepTitle number="2" title="Mensagem" description="Edite o assunto e o conteúdo. A personalização pelo nome é aplicada automaticamente." />
              <button
                type="button"
                onClick={() => setShowAi((value) => !value)}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-extrabold transition ${showAi ? "bg-indigo-600 text-white" : "border border-indigo-200 bg-indigo-50 text-indigo-700"}`}
              >
                <FaMagic /> {showAi ? "Fechar assistente" : "Criar com Nash IA"}
              </button>
            </div>

            {showAi && (
              <div className="mt-4 rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-blue-50 p-4">
                <div className="flex items-center gap-2 text-sm font-black text-slate-900"><FaMagic className="text-indigo-600" /> Nash IA</div>
                <p className="mt-1 text-xs text-slate-500">Descreva somente o que deseja mudar. A variável <b>{"{{primeiro_nome}}"}</b> será preservada.</p>
                <textarea rows={2} value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="Ex.: mensagem curta e profissional, agradecendo pela dedicação." className="mt-3 w-full resize-none rounded-xl border border-indigo-100 bg-white px-3.5 py-3 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap gap-1.5">{STYLE_PRESETS.map(([name]) => <button key={name} type="button" onClick={() => setStylePreset(name)} className={`rounded-full px-3 py-1.5 text-[11px] font-extrabold ${stylePreset === name ? "bg-indigo-600 text-white" : "border border-slate-200 bg-white text-slate-500"}`}>{name}</button>)}</div>
                  <button type="button" onClick={generate} disabled={busy === "ai"} className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-xs font-extrabold text-white shadow-sm disabled:opacity-50"><FaMagic /> {busy === "ai" ? "Criando..." : "Gerar mensagem"}</button>
                </div>
              </div>
            )}

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500">Tema do topo<input value={draft.theme} onChange={(e) => updateDraft("theme", e.target.value)} maxLength={120} className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold normal-case tracking-normal text-slate-800 outline-none focus:border-blue-400" /></label>
              <label className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500">Assunto<input value={draft.subject} onChange={(e) => updateDraft("subject", e.target.value)} maxLength={180} className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold normal-case tracking-normal text-slate-800 outline-none focus:border-blue-400" /></label>
              <label className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500 md:col-span-2">Pré-cabeçalho<input value={draft.previewText} onChange={(e) => updateDraft("previewText", e.target.value)} maxLength={300} className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm normal-case tracking-normal text-slate-800 outline-none focus:border-blue-400" /></label>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[10px] font-bold text-slate-500">
              <span className="mr-1">Variáveis:</span>
              {["{{primeiro_nome}}", "{{nome}}", "{{email}}", "{{data_aniversario}}", "{{matricula}}"].map((item) => <span key={item} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1">{item}</span>)}
            </div>

            <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
              <MarketingEmailEditor value={draft.html} onChange={(value) => updateDraft("html", value)} />
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <StepTitle number="3" title="Revisar e aplicar" description="Salve uma única vez. Depois disso o sistema mantém o módulo atualizado automaticamente." />
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-slate-50 p-3">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-2 font-extrabold text-slate-700 shadow-sm"><FaEnvelope className="text-blue-600" /> {eligibleCount - suppressedCount} receberão automaticamente</span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-2 font-extrabold text-slate-700 shadow-sm"><FaClock className="text-blue-600" /> {String(draft.sendHour).padStart(2, "0")}:00</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setPreviewOpen(true)} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs font-extrabold text-slate-600"><FaEye /> Ver prévia</button>
                <button type="button" onClick={save} disabled={busy === "save"} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-xs font-extrabold text-white shadow-md disabled:opacity-50"><FaSave /> {busy === "save" ? "Salvando..." : "Salvar e aplicar"}</button>
              </div>
            </div>
          </section>
        </main>
      </div>

      {previewOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm" onMouseDown={() => setPreviewOpen(false)}>
          <div className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-slate-100 p-5 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setPreviewOpen(false)} className="absolute right-7 top-7 z-20 grid h-9 w-9 place-items-center rounded-full bg-slate-900 text-xl font-black text-white">×</button>
            <div className="mx-auto overflow-hidden rounded-2xl bg-white shadow-sm">
              <div className="bg-[#071f54] px-7 py-5 pr-16 text-lg font-black text-white">{draft.theme || "Feliz aniversário!"}</div>
              <div className="p-8">
                <p className="text-xs font-bold text-slate-400">{draft.previewText}</p>
                <h3 className="mt-1 text-xl font-black text-slate-900">{draft.subject || "Sem assunto"}</h3>
                <div className="prose mt-6 max-w-none text-slate-800" dangerouslySetInnerHTML={{ __html: sanitizeDocumentTemplateHtml(draft.html) }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
